import React, { useRef } from "react";
import cn from "classnames";
import { css } from "@emotion/react";
import { CSS_COLORS } from "../../../../styles/styles-css-variables";
import { CSS_MQ } from "../../../../styles/breakpoints";

export function DiagramIconSvg(
  props: React.SVGProps<SVGSVGElement> & {
    onClickIcon: (idx: number) => void;
    elements: Array<string>;
    activeIdx: number;
    onChoiceHovered: (idx: number) => void;
  },
) {
  const { onClick, onClickIcon, elements, activeIdx, onChoiceHovered, ...rest } = props;
  const ref = useRef<SVGSVGElement | null>(null);

  const onClickEl: React.MouseEventHandler<SVGSVGElement> = e => {
    const { pageX, pageY } = e;

    const b = ref.current?.getBoundingClientRect();

    if (b) {
      const click = {
        x: pageX - b.left,
        y: pageY - b.top,
      };

      const x = (click.x / b.width) * 100;
      const y = (click.y / b.height) * 100;

      if (x < 100 / 3) {
        return onClickIcon(0);
      } else if (x < (100 / 3) * 2) {
        return onClickIcon(1);
      } else {
        return onClickIcon(2);
      }
    }
  };

  const y = 220;
  const h = 80;

  const w = 295;
  const partSize = (1.5 * w) / 6;

  const leftBorder = 0 - partSize;
  const rightBorder = w;

  const d = 6;

  const strokeColor = "#d8d8d8";

  const bodyOf = (idx: number) => {
    return (
      <div className={"_body"}>
        <p className={cn("label", { active: activeIdx === idx })}>{elements[idx]}</p>
      </div>
    );
  };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`${leftBorder} 0 ${1.5 * w} 295`}
      {...rest}
      ref={ref}
      onClick={onClickEl}
    >
      <foreignObject
        x={0 - partSize + d}
        y={y}
        width={2 * partSize - d}
        height={h}
        onClick={() => onClickIcon(0)}
        onMouseEnter={() => onChoiceHovered(0)}
      >
        {bodyOf(0)}
      </foreignObject>

      <foreignObject
        x={w / 2 - partSize}
        y={y}
        width={2 * partSize}
        height={h}
        onClick={() => onClickIcon(1)}
        onMouseEnter={() => onChoiceHovered(1)}
      >
        {bodyOf(1)}
      </foreignObject>

      <foreignObject
        x={rightBorder - partSize - d}
        y={y}
        width={2 * partSize - d}
        height={h}
        onClick={() => onClickIcon(2)}
        onMouseEnter={() => onChoiceHovered(2)}
      >
        {bodyOf(2)}
      </foreignObject>

      <g fill="none" fillRule="evenodd" stroke="none" strokeWidth="1">
        <g transform="translate(2.5 3)">
          <path
            stroke={strokeColor}
            strokeLinecap="square"
            strokeWidth="4"
            d="M18 201.5c59.129-5.855 98.795-12.855 119-21M154 173.5c64.093-30.662 106.093-82.995 126-157"
          />

          <path className="bar" stroke={strokeColor} d="M282 212.5L282 14.5" onClick={() => onClickIcon(2)} />
          <path className="bar" stroke={strokeColor} d="M145 212.5L145 183.5" onClick={() => onClickIcon(1)} />

          <circle
            cx="282"
            cy="8"
            r="8"
            stroke={"currentColor"}
            strokeWidth="3"
            className="icon icon-3"
            onClick={() => onClickIcon(2)}
          />
          <circle
            cx="145"
            cy="176"
            r="8"
            stroke={"currentColor"}
            strokeWidth="3"
            className="icon icon-2"
            onClick={() => onClickIcon(1)}
          />
          <circle
            cx="8"
            cy="203"
            r="8"
            stroke={"currentColor"}
            strokeWidth="3"
            className="icon icon-1"
            onClick={() => onClickIcon(0)}
          />
        </g>
      </g>
    </svg>
  );
}

export const svgStyles = css`
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  ._body {
    display: flex;
    justify-content: center;
    align-items: baseline;
    width: 100%;
    height: 100%;

    padding: 0 6px;
  }

  .label {
    fill: ${CSS_COLORS.White};

    width: 100%;
    line-height: 1;

    margin: 0 auto;
    text-align: center;

    font-size: 16px;

    ${CSS_MQ.atLeastLg} {
      font-size: 14px;
    }

    ${CSS_MQ.lowResNotebook} {
      font-size: 15px;
    }

    color: ${CSS_COLORS.Grey5};

    &.active {
      color: ${CSS_COLORS.White};
    }
  }

  width: 100%;
  height: 100%;

  .icon,
  .bar {
    pointer-events: all;
  }

  .icon-1,
  .icon-2,
  .icon-3 {
    color: ${CSS_COLORS.Grey};
    transition: 0.2s ease color;
  }

  &.checked-1 {
    .icon-1 {
      color: ${CSS_COLORS.White};

      fill: currentColor;
      stroke: currentColor;
    }
  }

  &.checked-2 {
    .icon-2 {
      color: ${CSS_COLORS.White};
      fill: currentColor;
      stroke: currentColor;
    }
  }

  &.checked-3 {
    .icon-3 {
      color: ${CSS_COLORS.White};
      fill: currentColor;
      stroke: currentColor;
    }
  }
`;
